// Radix Toast primitives + the <Toaster /> root that subscribes to
// the module-level store from ``hooks/use-toast``.
//
// Mount <Toaster /> once at the app root (main.tsx) — it provides the
// ToastViewport so toasts queued from anywhere stack and animate out
// uniformly, even across route changes.

import * as ToastPrimitive from "@radix-ui/react-toast";

import { removeToast, useToast, type ToastVariant } from "@/hooks/use-toast";

// Left-bordered toast surfaces — the bg is a tinted -bg token and the
// body text uses the high-contrast --foreground so it stays legible on
// the pastel surface in both themes. The accent border carries the
// semantic colour.
const VARIANT_CLASS: Record<ToastVariant, string> = {
  default:
    "border-l-4 border-accent border border-border bg-accent-light text-foreground",
  success:
    "border-l-4 border-status-done border border-border bg-status-done-bg text-foreground",
  error:
    "border-l-4 border-status-urgent border border-border bg-status-urgent-bg text-foreground",
};

export function Toaster() {
  const { toasts } = useToast();

  return (
    <ToastPrimitive.Provider swipeDirection="right" duration={Infinity}>
      {toasts.map((t) => (
        <ToastPrimitive.Root
          key={t.id}
          open={t.open}
          // Closing fires after the open=false animation finishes — at
          // that point we drop the record so the queue stays bounded.
          onOpenChange={(isOpen) => {
            if (!isOpen) removeToast(t.id);
          }}
          data-cy="toast"
          data-variant={t.variant}
          className={[
            "pointer-events-auto rounded-lg p-3 text-sm shadow-lg",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-right-4",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-right-4",
            VARIANT_CLASS[t.variant],
          ].join(" ")}
        >
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <ToastPrimitive.Title
                data-cy="toast-title"
                className="font-semibold"
              >
                {t.title}
              </ToastPrimitive.Title>
              {t.description ? (
                <ToastPrimitive.Description
                  data-cy="toast-description"
                  className="mt-0.5 text-xs opacity-90"
                >
                  {t.description}
                </ToastPrimitive.Description>
              ) : null}
            </div>
            <ToastPrimitive.Close
              aria-label="Close"
              data-cy="toast-close"
              className="rounded-md px-1.5 text-xs opacity-70 hover:opacity-100"
            >
              ×
            </ToastPrimitive.Close>
          </div>
        </ToastPrimitive.Root>
      ))}
      <ToastPrimitive.Viewport
        data-cy="toast-viewport"
        className="fixed bottom-4 right-4 z-[100] flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none"
      />
    </ToastPrimitive.Provider>
  );
}
